// Seasonal popup (snippets/seasonal-popup.liquid): picks one character at
// random, preloads both of its images, then after the configured delay
// bounces the first image in, swaps to the second image and pops the speech
// bubble. Classes drive the animation stages; see _seasonal-popup.scss.
if (!customElements.get('seasonal-popup')) {
  customElements.define(
    'seasonal-popup',
    class SeasonalPopup extends HTMLElement {
      static COOKIE_NAME = 'seasonal_popup_shown';
      static LOAD_TIMEOUT = 8000; // don't let a slow third-party script hold it back forever
      static AWAKE_AFTER = 1100; // ms after appearing before the swap + bubble
      static HIDE_DURATION = 400; // matches the is-hiding animation

      connectedCallback() {
        // Test mode: add ?seasonal_popup_all to any URL to show every
        // character at once, side by side, ignoring the cookie.
        const showAll = new URLSearchParams(window.location.search).has('seasonal_popup_all');

        if (this.dataset.hideMobile !== undefined && window.matchMedia('(max-width: 749px)').matches) return;
        if (this.dataset.designMode === undefined && !showAll && this.wasShownRecently()) return;

        let characters;
        try {
          characters = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        } catch (e) {
          return;
        }
        if (!characters.length) return;

        if (showAll && this.dataset.characterIndex === undefined) {
          characters.forEach((_, index) => {
            const clone = this.cloneNode(true);
            clone.dataset.characterIndex = index;
            clone.style.setProperty('--seasonal-popup-slot', index);
            this.before(clone);
          });
          this.remove();
          return;
        }

        const index = this.dataset.characterIndex ?? Math.floor(Math.random() * characters.length);
        this.character = characters[index];
        this.querySelector('.seasonal-popup__close').addEventListener('click', () => this.hide());

        const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
        Promise.all([this.wait(delay), this.loadImages()])
          .then(() => this.show())
          .catch(() => {});
      }

      disconnectedCallback() {
        clearTimeout(this.awakeTimer);
        clearTimeout(this.autohideTimer);
      }

      wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      }

      // Wait for the page itself to finish loading so the character images
      // don't compete with the page's own (LCP) images.
      async loadImages() {
        if (document.readyState !== 'complete') {
          await Promise.race([
            new Promise((resolve) => window.addEventListener('load', resolve, { once: true })),
            this.wait(SeasonalPopup.LOAD_TIMEOUT),
          ]);
        }

        const first = this.querySelector('.seasonal-popup__image--first');
        const second = this.querySelector('.seasonal-popup__image--second');
        const firstLoaded = this.loadImage(first, this.character.first);

        if (this.character.second) {
          // The second image is optional - if it fails, just skip the swap.
          await this.loadImage(second, this.character.second).catch(() => second.remove());
        } else {
          second.remove();
        }

        await firstLoaded;
      }

      // Plain load/error events rather than img.decode(): decode() can stay
      // pending indefinitely for images inside a hidden element / background tab.
      loadImage(img, src) {
        return new Promise((resolve, reject) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', reject, { once: true });
          img.src = src;
        });
      }

      show() {
        this.hidden = false;
        this.rememberShown();

        // Force a reflow so the entrance animation starts from its first frame.
        this.offsetWidth;
        this.classList.add('is-visible');

        this.awakeTimer = setTimeout(() => {
          this.classList.add('is-awake');

          const autohide = (parseInt(this.dataset.autohide, 10) || 0) * 1000;
          if (autohide) this.autohideTimer = setTimeout(() => this.hide(), autohide);
        }, SeasonalPopup.AWAKE_AFTER);
      }

      hide() {
        clearTimeout(this.awakeTimer);
        clearTimeout(this.autohideTimer);
        this.classList.add('is-hiding');
        setTimeout(() => {
          this.hidden = true;
        }, SeasonalPopup.HIDE_DURATION);
      }

      hours() {
        return parseInt(this.dataset.frequencyHours, 10) || 0;
      }

      wasShownRecently() {
        if (!this.hours()) return false;
        return document.cookie.split('; ').some((cookie) => cookie.startsWith(`${SeasonalPopup.COOKIE_NAME}=`));
      }

      // The cookie simply expires after the configured number of hours, after
      // which the popup shows again. 0 hours = show on every page load.
      rememberShown() {
        const hours = this.hours();
        if (!hours) return;
        document.cookie = `${SeasonalPopup.COOKIE_NAME}=1; max-age=${hours * 60 * 60}; path=/; SameSite=Lax`;
      }
    }
  );
}
